<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType } from '@/types/equip'
import { computed, ref } from 'vue'
import { equipIcon, equipTypeList } from '../../item/data'
import EquipInfo from '../../item/components/EquipInfo.vue'
import { getRiseStarPrice } from '../data'
import { socketEmitAsync } from '@/utils/web-socket'
import { formatNumberWithCommas } from '@/utils'
import { ElMessage } from 'element-plus'
import type { RiseStarRes } from '@/types/smithy'
import { getEquipName } from '@/utils/equip'
import { QuestionFilled } from '@element-plus/icons-vue'

const userStore = useUserStore()

const equipItemId = ref('')
const equipInfo = computed(() => {
  const res = userStore.itemList.find((item) => item.id === equipItemId.value)
  if (!res) {
    equipItemId.value = ''
    return undefined
  }
  const { name, type, attrs } = equipCollection[res.equipId]
  return { ...res, name, type, attrs }
})
const equippedList = computed(() =>
  equipTypeList.value.map((item) => {
    const itemId = userStore.equippedList[item]
    const equipInfo = userStore.itemList.find((item) => item.id === itemId)
    if (!equipInfo) return undefined
    const { name, type, attrs } = equipCollection[equipInfo.equipId]
    return { ...equipInfo, name, type, attrs }
  }),
)

const itemList = computed<EquipItemType[]>(() => {
  const items = userStore.itemList
  if (userStore.itemList.length === 0) return []
  return items
    .filter((item) => {
      const equippedIdList = Object.values(userStore.equippedList)
      if (isRising.value) {
        if (item.id === equipInfo.value?.id || item.equipId !== equipInfo.value?.equipId) return false
        if (item.isLock) return false
        if (isHide.value && (item.reinforcedLevel || item.starAttrs?.length)) return false
      }
      if (isRising.value && item.isLock) return false
      if (!['epic', 'unique', 'legend', 'antiquity', 'peerless'].includes(item.quality)) return false
      if (item.starAttrs?.length === 5) return false
      return !equippedIdList.includes(item.id)
    })
    .map((item) => {
      const { equipId } = item
      const { name, type, attrs } = equipCollection[equipId]
      return { ...item, name, type, attrs }
    })
})

function handleChoice(item: EquipItemType | undefined) {
  if (['normal', 'race', 'darkGold', 'myth', 'supreme'].includes(item?.quality || '')) return ElMessage.warning('仅有史诗、独特、传说、远古、绝世的装备可升星')
  equipItemId.value = item?.id || ''
  isRising.value = false
  useStone.value = false
}

const useStone = ref(false)
function beforeUseStoneChange() {
  if (!equipInfo.value) return false
  if (equipInfo.value.quality === 'antiquity') {
    ElMessage.warning('远古装备不可使用升星石')
    return false
  }
  if (userStore.consumables.riseStarStone <= 0) {
    ElMessage.warning('您未拥有升星石')
    return false
  }
  return true
}

const riseStarResShow = ref(false)
const isRising = ref(false)
const isHide = ref(true)
function handleRiseStarChoice() {
  if (!equipInfo.value) return
  const { id, equipId, quality, starAttrs } = equipInfo.value
  if (getRiseStarPrice(quality, starAttrs) > userStore.gold) {
    return ElMessage.warning('您的金币不足')
  }
  if (equipInfo.value.starAttrs?.length === 5) {
    return ElMessage.warning('已达到最高星级')
  }
  if (useStone.value) {
    return ElMessageBox.confirm('<p class="affix">升星操作将会<span class="hp">使用 1升星石</span>，是否确认升星？</p>', '提示', {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    })
      .then(() => {
        riseStarAction(id)
      })
      .catch(() => {})
  }
  if (!itemList.value.find((item) => item.equipId === equipId && item.id !== id && !useStone.value)) {
    return ElMessage.warning('背包内不存在相同装备，无法升星')
  }
  isRising.value = true
}
async function handleRiseStar(useItem: EquipItemType) {
  if (!equipInfo.value) return
  const { id } = equipInfo.value
  const action = await ElMessageBox.confirm('<p class="affix">升星操作将会<span class="hp">销毁</span>您选中的装备材料，是否确认升星？</p>', '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
  if (action === 'confirm') {
    if (useItem.reinforcedLevel || useItem.starAttrs?.length) {
      ElMessageBox.confirm(
        '<p class="affix">您选中了<span class="info">已强化/升星</span>的装备作为升星材料，该操作将导致其<span class="hp">销毁</span>，请再次确认是否进行操作？</p>',
        '提示',
        {
          type: 'warning',
          dangerouslyUseHTMLString: true,
          appendTo: 'body',
          confirmButtonClass: 'is-plain',
        },
      )
        .then(() => riseStarAction(id, useItem.id))
        .catch(() => {})
    } else {
      riseStarAction(id, useItem.id)
    }
  }
}
async function riseStarAction(mainId: string, subId?: string) {
  const { starAttr, userAttr } = await socketEmitAsync<RiseStarRes>({
    type: 'riseStarEquip',
    data: { mainId, subId, useStone: useStone.value },
  })

  const item = userStore.itemList.find((item) => item.id === mainId)
  if (!item || !equipInfo.value) return
  if (!item.starAttrs) item.starAttrs = []

  userStore.gold -= getRiseStarPrice(equipInfo.value.quality, equipInfo.value.starAttrs)
  userStore.userAttr = userAttr

  item.starAttrs.push(starAttr)
  if (!equipInfo.value.starAttrs) equipInfo.value.starAttrs = []
  equipInfo.value.starAttrs = item.starAttrs

  if (useStone.value) {
    userStore.consumables.riseStarStone--
    if (userStore.consumables.riseStarStone <= 0) useStone.value = false
  } else {
    const index = userStore.itemList.findIndex((item) => item.id === subId)
    userStore.itemList.splice(index, 1)
  }

  isRising.value = false
  riseStarResShow.value = true
}
</script>

<template>
  <div class="rise-star">
    <div class="rise-star-table border-wrap">
      <h4>锻造台</h4>
      <div class="rise-star-equip">
        <CommonBtn style="width: 100%" @done="handleChoice(undefined)">
          <span v-if="equipInfo" v-html="getEquipName(equipInfo, equipInfo.type)"></span>
          <span v-else>(未放入)</span>
        </CommonBtn>
      </div>
      <div class="help-info">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>升星说明</h4>
            <p>1. 消耗<span class="info">同名装备</span>可对装备进行升星。</p>
            <p>
              2. 仅有<span class="epic">史诗</span>、<span class="unique">独特</span>、<span class="legend">传说</span>、<span class="antiquity"
                >远古</span
              >、<span class="peerless">绝世</span>品质的装备可升星。
            </p>
            <p>3. 每次升星都会获取随机的基本属性，数值固定。</p>
            <p>4. 升星花费会随着装备升星次数的提升愈加昂贵。</p>
            <p>5. 升星操作会导致您选中的装备材料<span class="hp">销毁</span>。</p>
            <p>6. 使用<span class="info">升星石</span>时，可替代<span class="info">同名装备</span>进行升星。</p>
            <p>7. 装备最高升至 5星。</p>
          </div>
        </el-popover>
      </div>
      <template v-if="equipInfo">
        <div class="info-card">
          <p>
            <span>当前星级：{{ equipInfo.starAttrs?.length || 0 }}</span>
            <span>
              升星石（{{ userStore.consumables.riseStarStone }}）：
              <el-switch v-model="useStone" size="small" :before-change="beforeUseStoneChange" />
            </span>
          </p>
          <p v-if="equipInfo.starAttrs?.length === 5">(已满星)</p>
          <p v-else>升星花费：{{ formatNumberWithCommas(getRiseStarPrice(equipInfo.quality, equipInfo.starAttrs)) }} 金币</p>
        </div>
        <div class="btn-wrap">
          <el-button type="warning" plain @click="handleRiseStarChoice" v-show="!isRising">开始升星</el-button>
          <el-button type="danger" plain @click="handleChoice(undefined)" v-show="isRising">取消</el-button>
        </div>
      </template>
    </div>
    <div class="item-list border-wrap" style="flex-shrink: 0; margin-bottom: 10px" v-show="!isRising">
      <h4>已装备</h4>
      <div class="item-btn-wrap btn-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="(item, index) of equippedList" :key="index">
            <CommonBtn
              size="small"
              @done="handleChoice(item)"
              :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
              :type="item ? 'primary' : 'info'"
            >
              <span v-if="item" v-html="getEquipName(item, item.type)"></span>
              <span v-else>{{ equipIcon[equipTypeList[index]] }} (未装备)</span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="item-list border-wrap package">
      <h4>{{ isRising ? '选择装备作为材料' : '背包' }}</h4>
      <p class="cur-gold">
        <span :style="{ visibility: isRising ? 'unset' : 'hidden' }">
          隐藏已强化/升星
          <el-switch v-model="isHide" size="small" />
        </span>
        <span>💰 {{ formatNumberWithCommas(userStore.gold) }}</span>
      </p>
      <div class="item-btn-wrap btn-wrap item-list-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of itemList" :key="item.id">
            <CommonBtn
              size="small"
              @done="isRising ? handleRiseStar(item) : handleChoice(item)"
              :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
            >
              <span v-html="getEquipName(item, item.type)"></span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
      <p class="affix" style="text-align: center" v-show="isRising && itemList.length === 0">
        您背包中的相同装备<span class="info">已锁定</span>或<span class="info">已被隐藏</span>
      </p>
    </div>

    <el-dialog v-model="riseStarResShow" width="100%" style="max-width: 500px">
      <h2 class="affix">
        <span class="grow">升星成功</span>
      </h2>
      <template v-if="equipInfo">
        <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
          <template #reference>
            <div style="width: 50%; margin: 0 auto">
              <CommonBtn size="small" style="width: 100%">
                <span v-html="getEquipName(equipInfo, equipInfo.type)"></span>
              </CommonBtn>
            </div>
          </template>
          <EquipInfo :equip-info="equipInfo" :show-action="false" style="max-height: 60vh" />
        </el-popover>
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="riseStarResShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.rise-star {
  display: flex;
  flex-flow: column nowrap;
  overflow: hidden;
  .rise-star-table {
    position: relative;
    padding: 0 8px 10px 8px;
    margin-bottom: 10px;
    overflow: auto;
    background-color: rgba($color: #ffc773, $alpha: 0.2);
    &::-webkit-scrollbar {
      width: 5px;
    }
    &::-webkit-scrollbar-track {
      background-color: rgba($color: #fff, $alpha: 0.1);
    }
    &::-webkit-scrollbar-thumb {
      background-color: #b9b9b9;
      border-radius: 5px;
    }
    .rise-star-equip {
      border: 1px var(--fontColor) solid;
      padding: 6px 8px;
      border-radius: 4px;
      background-color: rgba($color: var(--fontColor), $alpha: 0.1);
    }
    .help-info {
      position: absolute;
      top: 5px;
      right: 10px;
    }
  }
  .item-list {
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    justify-content: unset;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .cur-gold {
      width: 100%;
      font-size: 14px;
      padding-right: 10px;
      margin: 0;
      padding: 0 10px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .item-btn-wrap {
      overflow: auto;
    }
    &.package {
      flex: 1;
      min-height: 135px;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
  .el-button + .el-button {
    margin-left: 0;
  }
}
</style>
